import { useCallback, useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import {
  deleteCartItem,
  updateCartItemQuantity,
  updateCurCartItemMaterial,
  updateCurProductMaterial,
} from '../../services';
import {
  deleteGuestCartItem,
  setCartItemQuantity,
} from '../../redux/slices/shoppingCartSlice';
import { ICartItem } from '../interfaces';
import { getCartItemValidationIssue } from '../../utils/cartValidation';
import { getVariantStockQuantity } from '../../utils/variantInventory';

export const useShoppingCartItem = (item: ICartItem) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const cartItemsCount = useAppSelector((state) => state.shoppingCart.addedTooCartProductCountAndIds.count);
  const userId = useAppSelector((state) => state.user.user?.id);
  const selectedMaterial = useAppSelector((state) =>
    state.shoppingCart.cartItems.find((cartItem) => cartItem.id === item.id)?.selectedMaterial
  );
  const selectedSize = useAppSelector((state) =>
    state.shoppingCart.cartItems.find((cartItem) => cartItem.id === item.id)?.selectedSize
  ) || '';
  const materialData = useAppSelector((state) =>
    state.shoppingCart.materialPriceAndImgForCart.find((entry) => entry.itemId === item.id)
  );
  const isMaterialLoading = useAppSelector(
    (state) => state.shoppingCart.loadingMaterialById[item.id]
  );
  const quantitySyncTimerRef = useRef<number | null>(null);
  const selectionSyncTimerRef = useRef<number | null>(null);
  const latestQuantityRef = useRef(item.quantity);
  const latestSelectionRef = useRef({
    selectedMaterial: selectedMaterial || '',
    selectedSize: selectedSize || '',
  });
  const persistedSelectionRef = useRef<string | null>(null);
  const hasPendingQuantitySyncRef = useRef(false);
  const hasPendingSelectionSyncRef = useRef(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const materialPriceFromData = materialData?.materialPrice || 0;
  const stockQuantity = getVariantStockQuantity(materialData, item.selectedSize, item.product.id);
  const validationIssue = getCartItemValidationIssue(item, materialData);
  const materialPrice = typeof item.materialPrice === 'number' ? item.materialPrice : 0;
  const productPrice = typeof item.product.price === 'number' ? item.product.price : 0;
  const hasLoadedMaterialData = materialData?.selectedMaterial === selectedMaterial;
  const materialDataLoading = isMaterialLoading || !hasLoadedMaterialData;
  const itemPrice = materialDataLoading ? 0 : materialPriceFromData || materialPrice || productPrice;

  useEffect(() => {
    latestQuantityRef.current = item.quantity;
  }, [item.quantity]);

  const flushSelectionSync = useCallback(() => {
    if (!userId || !hasPendingSelectionSyncRef.current) return;

    const { selectedMaterial: latestMaterial, selectedSize: latestSize } = latestSelectionRef.current;
    if (!latestMaterial) return;

    hasPendingSelectionSyncRef.current = false;
    persistedSelectionRef.current = `${item.id}:${latestMaterial}:${latestSize || ''}`;
    void dispatch(updateCurCartItemMaterial({
      cartItemId: item.id,
      selectedMaterial: latestMaterial,
      selectedSize: latestSize || '',
    })).unwrap().catch(() => {
      message.error('Failed to update cart item.');
    });
  }, [dispatch, item.id, userId]);

  useEffect(() => () => {
    if (quantitySyncTimerRef.current) {
      window.clearTimeout(quantitySyncTimerRef.current);
    }
    if (selectionSyncTimerRef.current) {
      window.clearTimeout(selectionSyncTimerRef.current);
    }
    if (userId && hasPendingQuantitySyncRef.current) {
      void dispatch(updateCartItemQuantity({
        cartItemId: item.id,
        quantity: latestQuantityRef.current,
      }));
    }
    flushSelectionSync();
  }, [dispatch, flushSelectionSync, item.id, userId]);

  useEffect(() => {
    if (!userId || !selectedMaterial) return;

    latestSelectionRef.current = {
      selectedMaterial,
      selectedSize: selectedSize || '',
    };

    const selectionKey = `${item.id}:${selectedMaterial}:${selectedSize || ''}`;

    if (persistedSelectionRef.current === null) {
      persistedSelectionRef.current = selectionKey;
      return;
    }

    if (persistedSelectionRef.current === selectionKey) return;

    if (selectionSyncTimerRef.current) {
      window.clearTimeout(selectionSyncTimerRef.current);
    }

    hasPendingSelectionSyncRef.current = true;
    selectionSyncTimerRef.current = window.setTimeout(() => {
      hasPendingSelectionSyncRef.current = false;
      persistedSelectionRef.current = selectionKey;
      void dispatch(updateCurCartItemMaterial({
        cartItemId: item.id,
        selectedMaterial,
        selectedSize: selectedSize || '',
      })).unwrap().catch(() => {
        message.error('Failed to update cart item.');
      });
    }, 350);

    return () => {
      if (selectionSyncTimerRef.current) {
        window.clearTimeout(selectionSyncTimerRef.current);
      }
    };
  }, [dispatch, item.id, selectedMaterial, selectedSize, userId]);

  const queueQuantitySync = useCallback((nextQuantity: number) => {
    latestQuantityRef.current = nextQuantity;
    if (!userId) return;

    hasPendingQuantitySyncRef.current = true;
    if (quantitySyncTimerRef.current) {
      window.clearTimeout(quantitySyncTimerRef.current);
    }

    quantitySyncTimerRef.current = window.setTimeout(() => {
      hasPendingQuantitySyncRef.current = false;
      void dispatch(updateCartItemQuantity({
        cartItemId: item.id,
        quantity: latestQuantityRef.current,
      })).unwrap().catch(() => {
        message.error('Failed to update quantity.');
      });
    }, 450);
  }, [dispatch, item.id, userId]);

  const applyQuantity = useCallback((nextQuantity: number) => {
    if (typeof stockQuantity === 'number' && nextQuantity > stockQuantity) {
      message.warning(`Only ${stockQuantity} item(s) available in stock.`);
      return;
    }

    const safeQuantity = Math.max(1, nextQuantity);
    dispatch(setCartItemQuantity({
      itemId: item.id,
      quantity: safeQuantity,
      persistGuest: !userId,
    }));
    queueQuantitySync(safeQuantity);
  }, [dispatch, item.id, queueQuantitySync, stockQuantity, userId]);

  const handleIncrementQuantity = useCallback(() => {
    applyQuantity(latestQuantityRef.current + 1);
  }, [applyQuantity]);

  const handleDecrementQuantity = useCallback(() => {
    if (latestQuantityRef.current <= 1) {
      setIsRemoveModalOpen(true);
      return;
    }

    applyQuantity(latestQuantityRef.current - 1);
  }, [applyQuantity]);

  const handleDeleteItem = useCallback(async () => {
    try {
      setIsRemoveModalOpen(false);
      if (userId) {
        await dispatch(deleteCartItem(item.id)).unwrap();
      } else {
        dispatch(deleteGuestCartItem(item.id));
      }
      if (cartItemsCount === 1) {
        message.warning('Your cart is empty, redirecting to the home page');
        navigate('/');
      }
    } catch {
      message.error('Failed to delete the item from the cart');
    }
  }, [cartItemsCount, dispatch, item.id, navigate, userId]);

  const handleImageClick = useCallback(() => {
    if (selectedMaterial) {
      dispatch(updateCurProductMaterial({ id: item.product.id, selectedMaterial }));
    }
    navigate(`/products/${item.product.id}`);
  }, [dispatch, item.product.id, navigate, selectedMaterial]);

  return {
    isRemoveModalOpen,
    itemPrice,
    materialDataLoading,
    selectedMaterial,
    selectedSize,
    stockQuantity,
    validationIssue,
    handleDeleteItem,
    handleDecrementQuantity,
    handleImageClick,
    handleIncrementQuantity,
    setIsRemoveModalOpen,
  };
};
